import { Calculator } from 'lucide-react';
import { Dropdown } from 'primereact/dropdown';
import React, { useState, useEffect } from 'react';
import { BranchLecturerRateEntity } from '../../../../../../api/types/lecturers/user-details-lecturer';
import { toast } from 'react-toastify';
import { updateLecturerRateAccountantOperation } from './operations/update-lecturer-rate-accountant-operation';

export enum LegalEntityType {
  EMPLOYEE = 'employee',
  VAT_REGISTERED_FREELANCER = 'vat-registered-freelancer',
  VAT_EXEMPT_FREELANCER = 'vat-exempt-freelancer',
  COMPANY = 'company',
  NONPROFIT = 'nonprofit',
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace LegalEntityType {
  export function getDisplayText(entityType: LegalEntityType): string {
    switch (entityType) {
      case LegalEntityType.COMPANY:
        return 'חברה בע״מ';
      case LegalEntityType.EMPLOYEE:
        return 'שכיר';
      case LegalEntityType.NONPROFIT:
        return 'עמותה';
      case LegalEntityType.VAT_EXEMPT_FREELANCER:
        return 'עוסק פתור';
      case LegalEntityType.VAT_REGISTERED_FREELANCER:
        return 'עוסק מורשה';

      default:
        return '';
    }
  }
}

export interface UserPageLecturerRateAccountantDataModel {
  rate: BranchLecturerRateEntity;
}

export const UserPageLecturerRateAccountant: React.FC<
  UserPageLecturerRateAccountantDataModel
> = ({ rate }) => {
  const [accountingData, setAccountingData] = useState<{
    [key: number]: {
      rateType: LegalEntityType | '';
      legalEntityId: string | null;
      reportingCode: string;
      accountingReportCodeTravel: string;
    };
  }>({});

  // Initialize accountingData with values from rate prop
  useEffect(() => {
    if (rate) {
      setAccountingData((prev) => ({
        ...prev,
        [rate.id]: {
          rateType: rate.legalEntityType || '',
          legalEntityId: rate.legalEntityId || null,
          reportingCode: rate.reportingCode || '',
          accountingReportCodeTravel: rate.accountingReportCodeTravel ?? '',
        },
      }));
    }
  }, [
    rate.id,
    rate.legalEntityType,
    rate.legalEntityId,
    rate.reportingCode,
    rate.accountingReportCodeTravel,
  ]);

  const getRateTypeOptions = () => [
    { label: 'בחר סוג תעריף', value: '' },
    { label: 'שכיר', value: LegalEntityType.EMPLOYEE },
    {
      label: 'עצמאי (מורשה)',
      value: LegalEntityType.VAT_REGISTERED_FREELANCER,
    },
    { label: 'עצמאי (פטור)', value: LegalEntityType.VAT_EXEMPT_FREELANCER },
    { label: 'חברה בע״מ', value: LegalEntityType.COMPANY },
    { label: 'עמותה', value: LegalEntityType.NONPROFIT },
  ];

  const getRegistrationNumberLabel = (rateType: LegalEntityType | '') => {
    return rateType === LegalEntityType.COMPANY ? 'ח.פ' : 'ח.ע';
  };

  const getRegistrationNumberPlaceholder = (rateType: LegalEntityType | '') => {
    return rateType === LegalEntityType.COMPANY
      ? 'הכנס מספר חברה פרטית'
      : 'הכנס מספר עמותה';
  };

  const requiresRegistrationNumber = (rateType: LegalEntityType | '') => {
    return (
      rateType === LegalEntityType.COMPANY ||
      rateType === LegalEntityType.NONPROFIT
    );
  };

  const saveAccountantData = async (
    rateId: number,
    legalEntityType: LegalEntityType,
    reportingCode: string,
    legalEntityId: string | null,
    accountingReportCodeTravel: string | null,
  ) => {
    await updateLecturerRateAccountantOperation(
      rate.branchId,
      rate.userId,
      rate.id,
      legalEntityType,
      reportingCode,
      legalEntityId,
      accountingReportCodeTravel,
    );
  };

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.5rem',
        backgroundColor: '#F8FAFC',
        borderRadius: '0.5rem',
        border: '1px solid #E2E8F0',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
        }}
      >
        <Calculator
          style={{
            width: '20px',
            height: '20px',
            color: '#3B82F6',
          }}
        />
        <h4
          style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: '#1E293B',
            margin: 0,
          }}
        >
          הנהלת חשבונות
        </h4>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '1rem',
        }}
      >
        {/* סוג יישות + קוד דיווח + קוד דיווח לנסיעות — one row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '1rem',
            alignItems: 'start',
            direction: 'rtl',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '0.5rem',
                textAlign: 'right',
              }}
            >
              סוג יישות
            </label>
            <Dropdown
              value={accountingData[rate.id]?.rateType || ''}
              onChange={(e) => {
                setAccountingData((prev) => ({
                  ...prev,
                  [rate.id]: {
                    ...prev[rate.id],
                    rateType: e.value,
                    legalEntityId: null, // Reset legal entity ID when type changes
                  },
                }));
              }}
              options={getRateTypeOptions()}
              placeholder="בחר סוג תעריף"
              appendTo="self"
              style={{
                width: '100%',
                textAlign: 'right',
                direction: 'rtl',
              }}
              panelStyle={{
                textAlign: 'right',
                direction: 'rtl',
              }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '0.5rem',
                textAlign: 'right',
              }}
            >
              קוד דיווח
            </label>
            <input
              type="text"
              value={accountingData[rate.id]?.reportingCode || ''}
              onChange={(e) => {
                setAccountingData((prev) => ({
                  ...prev,
                  [rate.id]: {
                    ...prev[rate.id],
                    reportingCode: e.target.value,
                  },
                }));
              }}
              placeholder="הכנס קוד דיווח"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                textAlign: 'right',
                direction: 'rtl',
              }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '0.5rem',
                textAlign: 'right',
              }}
            >
              קוד דיווח לנסיעות
            </label>
            <input
              type="text"
              maxLength={50}
              value={accountingData[rate.id]?.accountingReportCodeTravel ?? ''}
              onChange={(e) => {
                setAccountingData((prev) => ({
                  ...prev,
                  [rate.id]: {
                    ...prev[rate.id],
                    accountingReportCodeTravel: e.target.value,
                  },
                }));
              }}
              placeholder="הכנס קוד דיווח לנסיעות (אופציונלי)"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                textAlign: 'right',
                direction: 'rtl',
              }}
            />
          </div>
        </div>

        {/* Conditional Registration Number Input */}
        {requiresRegistrationNumber(accountingData[rate.id]?.rateType) && (
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '0.5rem',
                textAlign: 'right',
              }}
            >
              {getRegistrationNumberLabel(accountingData[rate.id]?.rateType)}
            </label>
            <input
              type="text"
              value={accountingData[rate.id]?.legalEntityId || ''}
              onChange={(e) => {
                setAccountingData((prev) => ({
                  ...prev,
                  [rate.id]: {
                    ...prev[rate.id],
                    legalEntityId: e.target.value,
                  },
                }));
              }}
              placeholder={getRegistrationNumberPlaceholder(
                accountingData[rate.id]?.rateType,
              )}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                textAlign: 'right',
                direction: 'rtl',
              }}
            />
          </div>
        )}
      </div>

      {/* Save Button */}
      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <button
          onClick={() => {
            // TODO: Implement save accounting data API call
            const currentData = accountingData[rate.id];
            if (currentData?.rateType && currentData?.reportingCode) {
              const travel = currentData.accountingReportCodeTravel?.trim();
              saveAccountantData(
                rate.id,
                currentData.rateType as LegalEntityType,
                currentData.reportingCode,
                currentData.legalEntityId,
                travel ? travel : null,
              );
            }
          }}
          disabled={
            !accountingData[rate.id]?.rateType ||
            !accountingData[rate.id]?.reportingCode
          }
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor:
              !accountingData[rate.id]?.rateType ||
              !accountingData[rate.id]?.reportingCode
                ? '#9CA3AF'
                : '#3B82F6',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor:
              !accountingData[rate.id]?.rateType ||
              !accountingData[rate.id]?.reportingCode
                ? 'not-allowed'
                : 'pointer',
            transition: 'background-color 0.2s',
          }}
          onMouseOver={(e) => {
            if (
              accountingData[rate.id]?.rateType &&
              accountingData[rate.id]?.reportingCode
            ) {
              e.currentTarget.style.backgroundColor = '#2563EB';
            }
          }}
          onMouseOut={(e) => {
            if (
              accountingData[rate.id]?.rateType &&
              accountingData[rate.id]?.reportingCode
            ) {
              e.currentTarget.style.backgroundColor = '#3B82F6';
            }
          }}
        >
          שמור נתוני חשבונות
        </button>
      </div>
    </div>
  );
};
